import React from "react";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import moment from "moment/moment";
import {JsonDataViewer} from "./JsonDataViewer";
import {DEFAULT_GLYCEMIA_UNIT, GLYCEMIA_UNITS} from "../pre-hospital-workflow/PreHospitalWorkflowConfig";

function formatValue(value) {
    if (value === null || value === undefined || value === "") {
        return "Non disponibile";
    }

    if (typeof value === "boolean") {
        return value ? "Sì" : "No";
    }

    if (typeof value === "number") {
        return String(value);
    }

    if (typeof value === "string") {
        const parsedDate = moment(value, moment.ISO_8601, true);
        if (parsedDate.isValid()) {
            return parsedDate.format("DD/MM/YYYY HH:mm");
        }
        return value;
    }

    return String(value);
}

function SummaryTable({title, rows}) {
    return (
        <Stack spacing={1.5}>
            <Typography variant="h6">{title}</Typography>
            <Table size="small" aria-label={title}>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.label} sx={{"&:last-child td, &:last-child th": {border: 0}}}>
                            <TableCell component="th" scope="row" sx={{width: "45%", pl: 0, fontWeight: 600}}>
                                {row.label}
                            </TableCell>
                            <TableCell sx={{pr: 0}}>{row.render ? row.render(row.value) : formatValue(row.value)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Stack>
    );
}

export function WorkflowOutputInputSummaryDialog({open, onClose, output}) {
    const anamnesis = output?.anamnesis ?? {};
    const parameters = output?.parameters ?? {};
    const scales = output?.scales ?? [];
    const dlvDebugInfo = output?.dlvDebugInfo ?? null;
    let anamnesisRows = [];

    if (anamnesis.symptomsOnsetTimeKnown) {
        anamnesisRows = [...anamnesisRows,
            {label: "È noto l'orario di esordio dei primi sintomi?", value: true},
            {label: "Data e ora di esordio dei sintomi", value: anamnesis.symptomsOnsetTime, render: formatValue},
        ];
    } else {
        anamnesisRows = [...anamnesisRows,
            {label: "È noto l'orario di esordio dei primi sintomi?", value: false},
            {label: "I sintomi si sono manifestati al risveglio?", value: anamnesis.wakeUpStroke},
        ];

        if (anamnesis.wakeUpStroke) {
            anamnesisRows = [...anamnesisRows,
                {label: "Data e ora in cui il paziente è andato a dormire", value: anamnesis.bedTime, render: formatValue},
            ];
        } else {
            anamnesisRows = [...anamnesisRows,
                {label: "Data e ora in cui è stato visto in salute per l'ultima volta", value: anamnesis.discoveryOrLastKnownWellTime, render: formatValue},
            ];
        }
    }

    anamnesisRows = [...anamnesisRows,
        {label: "Ha assunto ansiolitici nelle ultime 48 ore?", value: anamnesis.tookAnxiolytics48h},
        {label: "È un paziente epilettico?", value: anamnesis.isEpileptic},
        {label: "È un paziente cardiopatico che assume anticoagulanti?", value: anamnesis.tookAnticoagulants48h},
    ];

    const parameterRows = [
        {
            label: "Pressione sistolica minima",
            value: parameters.bloodPressureMin,
            render: (value) => value === null || value === undefined ? "Non disponibile" : `${value} mmHg`
        },
        {
            label: "Pressione diastolica massima",
            value: parameters.bloodPressureMax,
            render: (value) => value === null || value === undefined ? "Non disponibile" : `${value} mmHg`
        },
        {
            label: "Saturazione ossigeno",
            value: parameters.oxygenSaturation,
            render: (value) => value === null || value === undefined ? "Non disponibile" : `${value}%`
        },
        {
            label: "Glicemia",
            value: parameters.glycemia,
            render: (value) => value === null || value === undefined
                ? "Non disponibile"
                : `${value} ${(GLYCEMIA_UNITS[parameters.glycemiaUnit] ?? GLYCEMIA_UNITS[DEFAULT_GLYCEMIA_UNIT]).label}`
        },
    ];

    const scaleRows = scales.map((scale) => ({
        label: scale.name,
        value: scale,
        render: (value) => `${value.score} / ${value.maxScore}`
    }));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>Dati di input utilizzati</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Typography variant="body2" color="text.secondary">
                        La tabella seguente riassume i dati di anamnesi, parametri e scale utilizzati per la generazione dell'output.
                    </Typography>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Stack spacing={2} divider={<Divider flexItem/>}>
                            <SummaryTable title="Anamnesi" rows={anamnesisRows}/>
                            <SummaryTable title="Parametri" rows={parameterRows}/>
                            {scaleRows.length > 0 && <SummaryTable title="Scale" rows={scaleRows}/>}
                        </Stack>
                    </Paper>
                    {dlvDebugInfo && (
                        <Paper variant="outlined" sx={{p: 2}}>
                            <Stack spacing={1.5}>
                                <Typography variant="h6">Debug</Typography>
                                <Box>
                                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                                        Richiesta inviata al servizio DLV
                                    </Typography>
                                    <JsonDataViewer value={dlvDebugInfo.requestBodySent}/>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                                        Risposta ricevuta dal servizio DLV
                                    </Typography>
                                    <JsonDataViewer value={dlvDebugInfo.answerSetsReceived}/>
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            I valori mancanti vengono mostrati come non disponibili.
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Chiudi</Button>
            </DialogActions>
        </Dialog>
    );
}


